import { useNavigate } from "react-router-dom";
import { CalendarClock, Eye, EyeOff, Loader2, Square, Timer } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { FOCUS_QUICK_MINUTES, fmtLocalDay, fmtLocalTime, fmtMinutes, useFocus } from "@/context/FocusContext";
import { cn } from "@/lib/utils";

const minutesLabel = (m) => (m >= 60 && m % 60 === 0 ? `${m / 60} hour${m === 60 ? "" : "s"}` : `${m} min`);

/** Small SVG ring around the timer icon showing weekly goal progress (idle state only). */
const GoalRing = ({ progress }) => {
  const r = 9;
  const c = 2 * Math.PI * r;
  const p = Math.max(0, Math.min(1, progress || 0));
  return (
    <span className="relative inline-flex items-center justify-center w-5 h-5" data-testid="focus-goal-ring" aria-hidden="true">
      <svg viewBox="0 0 22 22" className="absolute inset-0 w-5 h-5 -rotate-90">
        <circle cx="11" cy="11" r={r} fill="none" stroke="currentColor" strokeWidth="2" className="text-border" />
        <circle
          cx="11" cy="11" r={r} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={c * (1 - p)} className="text-accent transition-[stroke-dashoffset] duration-300"
        />
      </svg>
      <Timer className="w-3 h-3 relative" />
    </span>
  );
};

/** Header indicator: quick start when idle, remaining time + peek/end while focus is on. */
export const FocusMenu = () => {
  const { status, active, peek, setPeek, busy, start, end } = useFocus();
  const navigate = useNavigate();
  if (!status) return null;

  const left = status.minutes_left || 0;
  const leftLabel = left >= 60 ? `${Math.floor(left / 60)}h ${left % 60}m` : `${left}m`;
  const next = status.next_window;
  const goal = status.goal_minutes || 0;
  const weekMinutes = status.week_minutes || 0;
  const streak = status.streak_weeks || 0;

  const togglePeek = () => {
    const nextPeek = !peek;
    setPeek(nextPeek);
    if (nextPeek) navigate("/inbox");
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant={active ? "default" : "ghost"}
          size="sm"
          className={cn("h-9 gap-1.5 rounded-full px-3", active && "bg-accent text-accent-foreground hover:bg-accent/90")}
          data-testid="focus-menu-button"
          aria-label={active ? `Focus on, ${leftLabel} left` : goal > 0 ? `Start focus. ${fmtMinutes(weekMinutes)} of ${fmtMinutes(goal)} protected this week` : "Start focus"}
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : !active && goal > 0 ? <GoalRing progress={weekMinutes / goal} /> : <Timer className="w-4 h-4" />}
          <span className="text-xs font-semibold tabular-nums" data-testid="focus-menu-label">
            {active ? leftLabel : <span className="hidden sm:inline">Focus</span>}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64 bg-popover">
        {active ? (
          <>
            <DropdownMenuLabel>
              <div className="text-sm font-medium">Focus on until {fmtLocalTime(status.until)}</div>
              <div className="text-xs text-muted-foreground font-normal" data-testid="focus-menu-held">
                {status.held_count > 0
                  ? `${status.held_count} new email${status.held_count === 1 ? "" : "s"} held`
                  : "Sync paused, nothing new lands until then"}
                {status.source === "window" ? " · scheduled" : ""}
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={togglePeek} data-testid="focus-menu-peek">
              {peek ? <EyeOff className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
              {peek ? "Stop peeking" : "Peek at held mail"}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={end} disabled={busy} data-testid="focus-menu-end">
              <Square className="w-4 h-4 mr-2" /> End focus now
            </DropdownMenuItem>
          </>
        ) : (
          <>
            <DropdownMenuLabel>
              <div className="text-sm font-medium">Start a focus session</div>
              <div className="text-xs text-muted-foreground font-normal">Holds new mail and pauses sync inside Discern.</div>
              {(goal > 0 || streak > 0) && (
                <div className="text-[11px] text-muted-foreground font-normal mt-1 tabular-nums" data-testid="focus-menu-goal">
                  {goal > 0 ? `${fmtMinutes(weekMinutes)} of ${fmtMinutes(goal)} protected this week` : `${fmtMinutes(weekMinutes)} protected this week`}
                  {streak > 0 ? ` · quiet streak ${streak} week${streak === 1 ? "" : "s"}` : ""}
                </div>
              )}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {FOCUS_QUICK_MINUTES.map((m) => (
              <DropdownMenuItem key={m} onClick={() => start(m)} disabled={busy} data-testid={`focus-menu-start-${m}`}>
                <Timer className="w-4 h-4 mr-2" /> Focus for {minutesLabel(m)}
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate("/settings?tab=focus")} data-testid="focus-menu-schedule">
              <CalendarClock className="w-4 h-4 mr-2" />
              <span className="flex-1">Focus schedule</span>
              {next && (
                <span className="text-[11px] text-muted-foreground" data-testid="focus-menu-next">
                  {next.window?.label ? `${next.window.label} · ` : ""}{fmtLocalDay(next.start)} {fmtLocalTime(next.start)}
                </span>
              )}
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
